import { cn } from "@tcg/ui";
import { Banknote, Building2, CreditCard, Printer, ShieldCheck, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MoneyText, useYen } from "@/components/MoneyText";
import type { Buylist, PayoutMethod } from "@/lib/types";

import type { IdentityStatus } from "./SellerPanel";

export const T_NUMBER = /^T\d{13}$/;

export interface PayoutState {
  method: PayoutMethod;
  taxFreeCheck: boolean;
  hasTNumber: boolean;
  tNumber: string;
  approvalToken: string | null;
}

export const INITIAL_PAYOUT: PayoutState = {
  method: "cash",
  taxFreeCheck: false,
  hasTNumber: false,
  tNumber: "",
  approvalToken: null,
};

export type AcceptBlocker =
  | "no_lines"
  | "identity_missing"
  | "identity_expired"
  | "tax_free_check"
  | "credit_needs_customer"
  | "t_number"
  | "manager";

/**
 * Why 「買取確定・支払」 is not possible yet — in the order staff fix things. The button stays off and
 * the first reason is shown as one sentence **before** anything is sent (T07-31 acceptance).
 */
export function acceptBlockers(input: {
  intake: Pick<Buylist, "lines" | "customerId" | "approval">;
  identity: IdentityStatus;
  payout: PayoutState;
  canApproveOverLimit: boolean;
}): AcceptBlocker[] {
  const { intake, identity, payout } = input;
  const out: AcceptBlocker[] = [];
  if (!intake.lines.length) {
    out.push("no_lines");
  }
  if (identity === "missing") {
    out.push("identity_missing");
  }
  if (identity === "expired") {
    out.push("identity_expired");
  }
  if (!payout.taxFreeCheck) {
    out.push("tax_free_check");
  }
  if (payout.method === "store_credit" && !intake.customerId) {
    out.push("credit_needs_customer");
  }
  if (payout.hasTNumber && !T_NUMBER.test(payout.tNumber.trim())) {
    out.push("t_number");
  }
  if (intake.approval?.overLimit && !input.canApproveOverLimit && !payout.approvalToken) {
    out.push("manager");
  }
  return out;
}

const METHODS: Array<{ id: PayoutMethod; icon: typeof Banknote }> = [
  { id: "cash", icon: Banknote },
  { id: "bank_transfer", icon: Building2 },
  { id: "store_credit", icon: CreditCard },
];

/**
 * 合計 + how the seller is paid (T07-31, design/05 §3.4): 現金 (from the drawer) / 振込 (the back
 * office pays later) / ストアクレジット (+ the shop's bonus, valid ≤ 6 months, needs a customer) —
 * each with the amount **pos-service** computed (`payoutOptions`); over the staff limit a banner and
 * 「店長承認」; the required tick 「免税購入品ではないことを確認した」; the seller's T-number (none →
 * 古物商特例). 「買取明細を印刷」 = the offer sheet; 「買取確定・支払」 stays off with the reason.
 */
export function PayoutPanel({
  intake,
  payout,
  onChange,
  blockers,
  busy,
  onOffer,
  onAccept,
  onApprove,
  canApproveOverLimit,
}: {
  intake: Buylist;
  payout: PayoutState;
  onChange: (changes: Partial<PayoutState>) => void;
  blockers: AcceptBlocker[];
  busy: boolean;
  onOffer: () => void;
  onAccept: () => void;
  onApprove: () => void;
  canApproveOverLimit: boolean;
}) {
  const { t } = useTranslation();
  const yen = useYen();
  const options = intake.payoutOptions;
  const amountOf = (method: PayoutMethod) =>
    method === "store_credit"
      ? options?.storeCredit.amount
      : method === "bank_transfer"
        ? options?.bankTransfer
        : options?.cash;
  const over = intake.approval?.overLimit === true;
  const first = blockers[0];

  return (
    <section aria-labelledby="payout-title" className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 id="payout-title" className="sr-only">
        {t("buylist.payout_title")}
      </h2>
      <p className="flex items-baseline justify-between text-2xl font-semibold">
        {t("buylist.total")}
        <MoneyText amount={intake.totalOffer} size="xl" />
      </p>
      <p className="text-sm text-muted-foreground">
        {t("buylist.card_count", { count: intake.lines.length })}
      </p>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-medium">{t("buylist.payout_method")}</legend>
        {METHODS.map(({ id, icon: Icon }) => {
          const amount = amountOf(id);
          return (
            <label
              key={id}
              className={cn(
                "flex min-h-pos-touch cursor-pointer items-center gap-3 rounded-md border px-3",
                payout.method === id && "border-2 border-primary bg-accent",
              )}
            >
              <input
                type="radio"
                name="payout-method"
                checked={payout.method === id}
                onChange={() => onChange({ method: id })}
                className="size-6"
              />
              <Icon aria-hidden className="size-6" />
              <span className="flex-1 text-lg">
                {t(`buylist.method_${id}`)}
                {id === "store_credit" && options ? (
                  <span className="block text-sm text-muted-foreground">
                    {t("buylist.credit_bonus", {
                      percent: options.storeCredit.bonusPercent,
                      months: options.storeCredit.validMonths,
                    })}
                  </span>
                ) : null}
                {id === "bank_transfer" ? (
                  <span className="block text-sm text-muted-foreground">
                    {t("buylist.bank_note")}
                  </span>
                ) : null}
              </span>
              {amount !== undefined ? <MoneyText amount={amount} size="lg" /> : null}
            </label>
          );
        })}
      </fieldset>
      {over ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-md border-2 border-status-problem bg-status-problem/10 p-3"
        >
          <TriangleAlert aria-hidden className="size-6 text-pos-problem" />
          <span className="flex-1">
            {t("buylist.over_limit", {
              limit: yen(intake.approval?.staffLimit ?? 0),
              today: yen(intake.approval?.staffTotalToday ?? 0),
            })}
          </span>
          {canApproveOverLimit || payout.approvalToken ? (
            <span className="flex items-center gap-1 font-semibold text-pos-ok">
              <ShieldCheck aria-hidden className="size-5" />
              {t("buylist.approved")}
            </span>
          ) : (
            <button
              type="button"
              onClick={onApprove}
              className="min-h-pos-touch rounded-md bg-primary px-5 text-lg font-semibold text-primary-foreground"
            >
              {t("buylist.manager_approve")}
            </button>
          )}
        </div>
      ) : null}
      <label className="flex min-h-pos-touch items-center gap-3 rounded-md border px-3">
        <input
          type="checkbox"
          checked={payout.taxFreeCheck}
          onChange={(e) => onChange({ taxFreeCheck: e.target.checked })}
          className="size-6"
        />
        <span className="text-lg">{t("buylist.tax_free_check")}</span>
      </label>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-medium">{t("buylist.seller_tax")}</legend>
        <div className="flex flex-wrap gap-2">
          {[false, true].map((has) => (
            <button
              key={String(has)}
              type="button"
              aria-pressed={payout.hasTNumber === has}
              onClick={() => onChange({ hasTNumber: has })}
              className={cn(
                "min-h-pos-touch rounded-md border px-4 text-lg",
                payout.hasTNumber === has && "border-primary bg-primary text-primary-foreground",
              )}
            >
              {has ? t("buylist.t_number_yes") : t("buylist.t_number_no")}
            </button>
          ))}
        </div>
        {payout.hasTNumber ? (
          <label className="flex flex-col gap-1">
            <span className="text-sm">{t("buylist.t_number")}</span>
            <input
              value={payout.tNumber}
              onChange={(e) => onChange({ tNumber: e.target.value.toUpperCase() })}
              placeholder="T1234567890123"
              aria-invalid={
                payout.tNumber && !T_NUMBER.test(payout.tNumber.trim()) ? true : undefined
              }
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
        ) : (
          <p className="text-sm text-muted-foreground">{t("buylist.kobutsu_special")}</p>
        )}
      </fieldset>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onOffer}
          disabled={busy || !intake.lines.length}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border px-5 text-lg disabled:opacity-50"
        >
          <Printer aria-hidden className="size-5" />
          {t("buylist.print_offer")}
        </button>
        <button
          type="button"
          onClick={onAccept}
          disabled={busy || blockers.length > 0}
          aria-describedby={first ? "accept-blocker" : undefined}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md bg-primary px-5 text-xl font-semibold text-primary-foreground disabled:opacity-50"
        >
          {busy ? t("app.saving") : t("buylist.accept")}
        </button>
        {first ? (
          <p id="accept-blocker" className="text-pos-problem">
            {t(`buylist.blocker_${first}`)}
          </p>
        ) : null}
      </div>
    </section>
  );
}
